/*
 * 模块: Design Tokens - Typography
 * 职责: 字体系统定义
 * 依赖: 无
 * 导出: typography
 * 限制: 文件<200行
 */

/**
 * 字体家族
 */
export const fontFamily = {
  /** 系统字体（主体文本） */
  base: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
  /** 等宽字体（代码） */
  mono: `'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace`,
  /** 标题字体 */
  heading: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
} as const;

/**
 * 字体尺寸（8级缩放系统）
 */
export const fontSize = {
  /** 12px - 辅助文字 */
  xs: '12px',
  /** 13px - 小型文字 */
  sm: '13px',
  /** 14px - 标准文字 */
  base: '14px',
  /** 16px - 正文 */
  md: '16px',
  /** 18px - 小标题 */
  lg: '18px',
  /** 20px - 标题 */
  xl: '20px',
  /** 24px - 大标题 */
  '2xl': '24px',
  /** 30px - 特大标题 */
  '3xl': '30px',
  /** 36px - 巨大标题 */
  '4xl': '36px',
  /** 48px - 超巨大标题 */
  '5xl': '48px',
} as const;

/**
 * 字重
 */
export const fontWeight = {
  /** 300 - 细体 */
  light: 300,
  /** 400 - 常规 */
  regular: 400,
  /** 500 - 中等 */
  medium: 500,
  /** 600 - 半粗 */
  semibold: 600,
  /** 700 - 粗体 */
  bold: 700,
  /** 800 - 特粗 */
  extrabold: 800,
} as const;

/**
 * 行高
 */
export const lineHeight = {
  /** 1.2 - 紧凑 */
  tight: 1.2,
  /** 1.4 - 较紧 */
  snug: 1.4,
  /** 1.5 - 标准 */
  base: 1.5,
  /** 1.6 - 较松 */
  relaxed: 1.6,
  /** 1.8 - 松散 */
  loose: 1.8,
  /** 2.0 - 超松 */
  extraLoose: 2.0,
} as const;

/**
 * 字母间距
 */
export const letterSpacing = {
  /** -0.02em - 紧凑 */
  tighter: '-0.02em',
  /** -0.01em - 较紧 */
  tight: '-0.01em',
  /** 0 - 标准 */
  normal: '0',
  /** 0.01em - 较松 */
  wide: '0.01em',
  /** 0.02em - 松散 */
  wider: '0.02em',
  /** 0.05em - 超松 */
  widest: '0.05em',
} as const;

/**
 * 文字样式预设
 */
export const textStyles = {
  /** 页面主标题 */
  h1: {
    fontSize: fontSize['4xl'],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  /** 二级标题 */
  h2: {
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  /** 三级标题 */
  h3: {
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  /** 四级标题 */
  h4: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  /** 五级标题 */
  h5: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.base,
    letterSpacing: letterSpacing.normal,
  },
  /** 六级标题 */
  h6: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.base,
    letterSpacing: letterSpacing.normal,
  },
  /** 正文 */
  body: {
    fontSize: fontSize.base,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.relaxed,
    letterSpacing: letterSpacing.normal,
  },
  /** 小型文本 */
  small: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.base,
    letterSpacing: letterSpacing.normal,
  },
  /** 辅助文本 */
  caption: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.base,
    letterSpacing: letterSpacing.wide,
  },
} as const;

/**
 * 导出所有字体设置
 */
export const typography = {
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  textStyles,
} as const;

export default typography;
